import { NavLink } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useNavigate } from 'react-router-dom';
import { 
  House, 
  WarningCircle, 
  MagnifyingGlass, 
  CheckSquare, 
  Images, 
  ShieldCheck, 
  EnvelopeSimple, 
  ChartBar, 
  Package, 
  Lightning, 
  Gear,
  SignOut,
  Moon,
  Sun,
  Cube,
  Camera,
  Question
} from '@phosphor-icons/react';
import { NotificationBell } from './NotificationBell';
import GlobalSearch from './GlobalSearch';

export default function Sidebar({ onNavigate }) {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const navItems = [
    { path: '/dashboard', icon: House, label: 'Dashboard' },
    { path: '/incidents', icon: WarningCircle, label: 'Notifications' },
    { path: '/investigations', icon: MagnifyingGlass, label: 'Investigations' },
    { path: '/tasks', icon: CheckSquare, label: 'Tasks' },
    { path: '/evidence', icon: Images, label: 'Evidence' },
    { path: '/safety-recommendations', icon: ShieldCheck, label: 'Recommendations' },
    { path: '/correspondence', icon: EnvelopeSimple, label: 'Correspondence' },
    { path: '/inventory', icon: Package, label: 'Inventory' },
    { path: '/reports', icon: ChartBar, label: 'Reports' },
    { path: '/ar-identify', icon: Camera, label: 'AR Identify' },
    { path: '/parts', icon: Cube, label: 'Parts DB' },
    { path: '/settings', icon: Gear, label: 'Settings' },
    { path: '/help', icon: Question, label: 'Help' },
  ];

  return (
    <div className="w-64 h-full bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900 text-white flex flex-col shrink-0">
      {/* Logo */}
      <div className="px-3 py-5 border-b border-slate-700">
        <div className="flex items-center justify-center">
          <img src="/roottrace-logo-dark.png" alt="RootTrace" className="w-full max-w-[180px] h-auto object-contain" />
        </div>
      </div>

      {/* Notification Bell */}
      <div className="px-3 py-2 border-b border-slate-700 flex justify-end">
        <NotificationBell />
      </div>

      {/* Global Search */}
      <div className="px-3 py-2 border-b border-slate-700">
        <GlobalSearch />
      </div>

      {/* Navigation */}
      <nav className="flex-1 py-4 px-3 space-y-1 overflow-y-auto">
        {navItems.map((item) => (
          <NavLink
            key={item.path}
            to={item.path}
            className={({ isActive }) =>
              `flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200 ${
                isActive
                  ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg'
                  : 'text-slate-300 hover:bg-white/5 hover:text-white'
              }`
            }
            data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
            onClick={onNavigate}
          >
            <item.icon size={20} weight="duotone" />
            <span className="text-sm font-medium">{item.label}</span>
          </NavLink>
        ))}
      </nav>

      {/* Theme Toggle */}
      <div className="px-3 pb-2">
        <button
          onClick={toggleTheme}
          className="w-full flex items-center gap-3 px-4 py-2.5 rounded-lg text-slate-300 hover:bg-white/5 hover:text-white transition-all duration-200"
          data-testid="theme-toggle"
        >
          {theme === 'dark' ? <Sun size={20} weight="duotone" /> : <Moon size={20} weight="duotone" />}
          <span className="text-sm font-medium">{theme === 'dark' ? 'Light Mode' : 'Dark Mode'}</span>
        </button>
      </div>

      {/* User Profile */}
      <div className="p-4 border-t border-slate-700">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-cyan-400 to-blue-500 flex items-center justify-center text-white font-semibold">
            {user?.name?.charAt(0) || 'A'}U
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium text-white truncate">{user?.name || 'User'}</div>
            <div className="text-xs text-slate-400 capitalize">{user?.role || 'Role'}</div>
          </div>
        </div>
        <button
          onClick={handleLogout}
          className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
          data-testid="logout-button"
        >
          <SignOut size={16} />
          <span className="text-sm font-medium">Logout</span>
        </button>
      </div>
    </div>
  );
}
